import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './Modal.css';

function EditCatchModal({ onClose, onSuccess }) {
  const [anglerSearch, setAnglerSearch] = useState('');
  const [anglers, setAnglers] = useState([]);
  const [selectedAngler, setSelectedAngler] = useState(null);
  const [catches, setCatches] = useState([]);
  const [selectedCatch, setSelectedCatch] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [deletingCatchId, setDeletingCatchId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Edit form fields
  const [round, setRound] = useState(1);
  const [sharkId, setSharkId] = useState('');
  const [sharkSearch, setSharkSearch] = useState('');
  const [availableSharks, setAvailableSharks] = useState([]);
  const [selectedShark, setSelectedShark] = useState(null);

  useEffect(() => {
    if (anglerSearch.length >= 2) {
      searchAnglers();
    } else {
      setAnglers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anglerSearch]);

  const searchAnglers = async () => {
    try {
      const response = await api.get(`/anglers/?search=${anglerSearch}`);
      setAnglers(response.data.anglers);
    } catch (error) {
      console.error('Error searching anglers:', error);
    }
  };

  const selectAngler = async (angler) => {
    setSelectedAngler(angler);
    setAnglers([]);
    setAnglerSearch(`${angler.first_name} ${angler.last_name}`.trim() || angler.license_number);
    setDeletingCatchId(null);
    setMessage('');

    try {
      const response = await api.get(`/catches/angler/${angler.id}`);
      setCatches(response.data);
    } catch (error) {
      console.error('Error loading catches:', error);
      setMessage('Error loading catches for this angler.');
      setMessageType('error');
    }
  };

  const selectCatchForEdit = (catchItem) => {
    setSelectedCatch(catchItem);
    setRound(catchItem.round);
    setSharkId(catchItem.shark_id);
    setMessage('');
    setEditMode(true);
  };

  const startAddMode = async () => {
    setRound(1);
    setSharkId('');
    setSharkSearch('');
    setSelectedShark(null);
    setMessage('');
    setAddMode(true);

    try {
      const sharksResponse = await api.get('/sharks/', { params: { round: 1 } });
      setAvailableSharks(sharksResponse.data);
    } catch (error) {
      console.error('Error loading sharks:', error);
      setAvailableSharks([]);
    }
  };

  const handleRoundChange = async (newRound) => {
    setRound(newRound);
    setSharkId('');
    setSharkSearch('');
    setSelectedShark(null);

    try {
      const sharksResponse = await api.get('/sharks/', { params: { round: newRound } });
      setAvailableSharks(sharksResponse.data);
    } catch (error) {
      console.error('Error loading sharks:', error);
      setAvailableSharks([]);
    }
  };

  const selectShark = (shark) => {
    setSelectedShark(shark);
    setSharkId(shark.id);
    setSharkSearch(shark.shark_id);
  };

  const formatApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item?.msg) return item.msg;
          return JSON.stringify(item);
        })
        .join('; ');
    }
    if (detail?.msg) return detail.msg;
    return JSON.stringify(detail);
  };

  const resolveSharkDatabaseId = async () => {
    const sharkInput = String(sharkId ?? '').trim();
    if (!sharkInput) {
      throw new Error('Shark ID is required');
    }

    const numericId = parseInt(sharkInput, 10);
    if (!Number.isNaN(numericId) && String(numericId) === sharkInput) {
      return numericId;
    }

    const sharksResponse = await api.get('/sharks/', { params: { round } });
    const matchedShark = sharksResponse.data.find(
      (shark) => shark.shark_id.toUpperCase() === sharkInput.toUpperCase()
    );

    if (!matchedShark) {
      throw new Error(`Shark '${sharkInput}' was not found in round ${round}`);
    }

    return matchedShark.id;
  };

  const handleUpdateCatch = async () => {
    if (!adminPassword) {
      setMessage('Admin password is required.');
      setMessageType('error');
      return;
    }

    try {
      const resolvedSharkId = await resolveSharkDatabaseId();

      const formData = new FormData();
      formData.append('password', adminPassword);
      formData.append('angler_id', selectedCatch.angler_id);
      formData.append('shark_id', resolvedSharkId);
      formData.append('round', round);

      await api.put(`/admin/catches/${selectedCatch.id}`, formData);

      setEditMode(false);
      setAdminPassword('');
      setMessage('Catch updated successfully.');
      setMessageType('success');

      selectAngler(selectedAngler);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error updating catch:', error);
      setMessage(formatApiError(error, 'Error updating catch.'));
      setMessageType('error');
    }
  };

  const handleAddCatch = async () => {
    if (!adminPassword) {
      setMessage('Admin password is required.');
      setMessageType('error');
      return;
    }

    if (!sharkId && sharkId !== 0) {
      setMessage('Please select a shark from the list.');
      setMessageType('error');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('password', adminPassword);
      formData.append('angler_id', selectedAngler.id);
      formData.append('shark_id', sharkId);
      formData.append('round', round);

      await api.post('/admin/catches/', formData);

      setAddMode(false);
      setAdminPassword('');
      setSharkId('');
      setSharkSearch('');
      setSelectedShark(null);
      setMessage('Catch added successfully.');
      setMessageType('success');

      selectAngler(selectedAngler);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding catch:', error);
      setMessage(formatApiError(error, 'Error adding catch.'));
      setMessageType('error');
    }
  };

  const handleDeleteCatch = async (catchId) => {
    if (!adminPassword) {
      setMessage('Enter admin password below before deleting.');
      setMessageType('error');
      setDeletingCatchId(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('password', adminPassword);

      await api.delete(`/catches/${catchId}`, { data: formData });

      setDeletingCatchId(null);
      setMessage('Catch deleted successfully.');
      setMessageType('success');

      selectAngler(selectedAngler);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error deleting catch:', error);
      setMessage(formatApiError(error, 'Error deleting catch.'));
      setMessageType('error');
      setDeletingCatchId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
        <h2>Manage Catch Data</h2>

        {!editMode && !addMode ? (
          <>
            <div className="form-group">
              <label>Search Angler by Name or License:</label>
              <input
                type="text"
                value={anglerSearch}
                onChange={(e) => setAnglerSearch(e.target.value)}
                placeholder="Type to search..."
                autoFocus
              />
            </div>

            {anglers.length > 0 && (
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ccc', padding: '5px' }}>
                {anglers.map((angler) => (
                  <div
                    key={angler.id}
                    onClick={() => selectAngler(angler)}
                    style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {angler.first_name} {angler.last_name} - {angler.license_number}
                  </div>
                ))}
              </div>
            )}

            {selectedAngler && (
              <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3>Catches for {selectedAngler.first_name} {selectedAngler.last_name}</h3>
                  <button
                    className="btn btn-success"
                    onClick={startAddMode}
                    style={{ padding: '6px 12px', fontSize: '14px' }}
                  >
                    + Add New Catch
                  </button>
                </div>
                {catches.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Round</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Shark ID</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Weight</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catches.map((catchItem) => (
                        <tr key={catchItem.id}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{catchItem.round}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{catchItem.shark_id}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {catchItem.shark
                              ? `${catchItem.shark.weight_lbs} lbs ${catchItem.shark.weight_oz} oz`
                              : 'Loading...'}
                          </td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            <button
                              className="btn btn-primary"
                              style={{ marginRight: '5px', padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => selectCatchForEdit(catchItem)}
                            >
                              Edit
                            </button>
                            {deletingCatchId === catchItem.id ? (
                              <>
                                <span style={{ fontSize: '12px', marginRight: '4px' }}>Confirm?</span>
                                <button
                                  className="btn btn-danger"
                                  style={{ padding: '4px 8px', fontSize: '12px', marginRight: '4px' }}
                                  onClick={() => handleDeleteCatch(catchItem.id)}
                                >
                                  Yes
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  onClick={() => setDeletingCatchId(null)}
                                >
                                  No
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                onClick={() => setDeletingCatchId(catchItem.id)}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {catches.length === 0 && <p>No catches found for this angler.</p>}

                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Admin Password (required for delete):</label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => { setAdminPassword(e.target.value); setMessage(''); }}
                    placeholder="Enter admin password"
                    autoComplete="current-password"
                  />
                </div>
              </div>
            )}
          </>
        ) : addMode ? (
          <div>
            <h3>Add New Catch for {selectedAngler.first_name} {selectedAngler.last_name}</h3>
            <div className="form-group">
              <label>Round:</label>
              <select
                value={round}
                onChange={(e) => handleRoundChange(parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
              >
                <option value={1}>Round 1</option>
                <option value={2}>Round 2</option>
                <option value={3}>Round 3</option>
              </select>
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                Select the round, then search for a shark
              </small>
            </div>
            <div className="form-group">
              <label>Search for Shark by ID:</label>
              <input
                type="text"
                value={sharkSearch}
                onChange={(e) => setSharkSearch(e.target.value.toUpperCase())}
                placeholder="Type shark ID (e.g., F9)"
              />
            </div>

            {availableSharks.filter(s => s.shark_id.toUpperCase().includes(sharkSearch)).length > 0 && sharkSearch && (
              <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ccc', padding: '5px', marginBottom: '10px' }}>
                {availableSharks.filter(s => s.shark_id.toUpperCase().includes(sharkSearch)).map((shark) => {
                  const isSelected = selectedShark?.id === shark.id;
                  return (
                    <div
                      key={shark.id}
                      onClick={() => selectShark(shark)}
                      style={{
                        padding: '8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        backgroundColor: isSelected ? '#d4edda' : 'white'
                      }}
                    >
                      {shark.shark_id} - {shark.weight_lbs} lbs {shark.weight_oz} oz (Round {shark.round})
                    </div>
                  );
                })}
              </div>
            )}

            {selectedShark && (
              <div style={{ padding: '10px', backgroundColor: '#d4edda', borderRadius: '4px', marginBottom: '10px' }}>
                <strong>Selected Shark:</strong> {selectedShark.shark_id} - {selectedShark.weight_lbs} lbs {selectedShark.weight_oz} oz
              </div>
            )}

            <div className="form-group">
              <label>Admin Password:</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => { setAdminPassword(e.target.value); setMessage(''); }}
                autoFocus
                autoComplete="current-password"
              />
            </div>
            {message && <p className={`modal-message ${messageType}`}>{message}</p>}
            <div className="modal-actions">
              <button className="btn btn-success" onClick={handleAddCatch}>Add Catch</button>
              <button className="btn btn-secondary" onClick={() => { setAddMode(false); setMessage(''); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            <h3>Edit Catch</h3>
            <div className="form-group">
              <label>Round:</label>
              <input
                type="number"
                min="1"
                max="3"
                value={round}
                onChange={(e) => setRound(parseInt(e.target.value))}
              />
            </div>
            <div className="form-group">
              <label>Shark ID:</label>
              <input
                type="text"
                value={sharkId}
                onChange={(e) => setSharkId(e.target.value)}
                placeholder="Database ID (e.g., 42) or shark code (e.g., F9)"
              />
            </div>
            <div className="form-group">
              <label>Admin Password:</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => { setAdminPassword(e.target.value); setMessage(''); }}
                autoFocus
                autoComplete="current-password"
              />
            </div>
            {message && <p className={`modal-message ${messageType}`}>{message}</p>}
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleUpdateCatch}>Save Changes</button>
              <button className="btn btn-secondary" onClick={() => { setEditMode(false); setMessage(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {!editMode && !addMode && (
          <>
            {message && <p className={`modal-message ${messageType}`}>{message}</p>}
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EditCatchModal;
